import React, { useCallback, useEffect } from 'react';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { AuthHeader, AuthMarketing, Button, ButtonRetry, Form, LayoutSplit, Link } from 'components';
import AuthCodeInput from 'components/Input/AuthCodeInput';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import Validations from 'lib/Validations';
import { IApiErrorPayloadWithCode } from 'types';
import { AuthContainer, AuthContent, AuthContentInner } from './styles';

const StyledLink = styled(Link)`
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(13.75)};
`;

const StyledButtonRetry = styled(ButtonRetry)`
  margin-top: ${toRem(33)};
`;

const StyledButton = styled(Button)`
  margin-top: ${toRem(33)};
`;

interface IPasswordConfirmIdentityProps {
  subtitle: string;
  emailOrMobileNumber?: string;
  onBack: () => void;
  onSubmit: (verificationCode: string) => void;
  apiErrors: IApiErrorPayloadWithCode;
  isLoading: boolean;
  isSmsResubmitLoading: boolean;
  resendSms: () => void;
  onClear?: () => void;
}

const PasswordConfirmIdentity = ({ subtitle, emailOrMobileNumber, onBack, onSubmit, apiErrors, isLoading, isSmsResubmitLoading, resendSms, onClear }: IPasswordConfirmIdentityProps) => {
  const validationSchema = Yup.object().shape({
    code: Validations.verificationCodeValidationSchema,
  });

  const { handleSubmit, dirty, isValid, setFieldValue } = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: (values: { code: string }) => {
      onSubmit(values.code);
    },
    validateOnChange: true,
    validationSchema,
  });

  const clearForm = () => onClear && onClear();
  const cbOnClear = useCallback(clearForm, []);

  useEffect(() => {
    cbOnClear();
    return () => {
      cbOnClear();
    };
  }, [cbOnClear]);

  return (
    <LayoutSplit
      left={
        <AuthContainer data-cy="reset-password-confirm-identity-form">
          <AuthContent className="email-verify">
            <AuthContentInner>
              <AuthHeader data-cy="confirm-identity-header" title="Confirm Your Identity" subtitle={subtitle} value={emailOrMobileNumber} onBack={onBack} />
              <Form onSubmit={handleSubmit}>
                <AuthCodeInput errorMessages={apiErrors?.errorMessages} onChange={(val) => setFieldValue('code', val)} />
                <StyledButton data-cy="submit-button" type="submit" label="Continue" disabled={isLoading || !(isValid && dirty)} isProcessing={isLoading} />
              </Form>
              <StyledButtonRetry dataCy="resend-verification-code" label="Resend verification code" onClick={resendSms} isLoading={isSmsResubmitLoading} />
              <StyledLink as="a" target="_blank" rel="noopener noreferrer" href={appConfig.LINKS.SUPPORT}>
                Need some help?
              </StyledLink>
            </AuthContentInner>
          </AuthContent>
        </AuthContainer>
      }
      right={<AuthMarketing />}
    />
  );
};

export default PasswordConfirmIdentity;
